import { useCallback, useState } from 'react';
import { useAuth } from '../lib/auth';
import { apiBaseUrl } from '../lib/apiBaseUrl';
import { capture } from '../lib/analytics';
import { openStripeRedirect, type StripeRedirectTarget } from '../lib/stripeRedirect';
import { log } from '@/lib/logger';
import { useAppSession } from './useAppSession';

/**
 * Opens a Stripe-hosted page for the active team and reports the two things a
 * button needs: which target is in flight (`pendingKey`: the plan slug, or
 * 'portal') and why the last attempt failed (`errorCode`).
 *
 * `pendingKey` is NOT cleared on success: the tab is navigating away, and
 * re-enabling the button for the last few hundred milliseconds invites the
 * second press that would open a second Stripe session.
 */
export function useStripeRedirect(): {
  open: (target: StripeRedirectTarget) => Promise<void>;
  pendingKey: string | null;
  errorCode: string | null;
} {
  const { getToken } = useAuth();
  const { data: session } = useAppSession();
  const teamId = session?.team?.id;
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const open = useCallback(
    async (target: StripeRedirectTarget) => {
      setPendingKey(target.kind === 'checkout' ? target.planSlug : 'portal');
      setErrorCode(null);
      const result = await openStripeRedirect({ apiBase: apiBaseUrl(), teamId, getToken, target });
      if (result.ok) return;
      log.warn('[useStripeRedirect] could not open the Stripe page', { kind: target.kind, code: result.code });
      capture('stripe_redirect_failed', { kind: target.kind, code: result.code });
      setErrorCode(result.code);
      setPendingKey(null);
    },
    [getToken, teamId],
  );

  return { open, pendingKey, errorCode };
}
